import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import Load from './Load';
import { useAuth } from '../context/AuthContext';
import { usePagamentoComStatusContaIugu } from '../hooks/usePagamentoComStatusContaIugu';
import { cancelarPagamentoEInscricoes } from '../services/pagamentoInscricaoService';
import { consentirCompartilharInformacoes } from '../services/inscricaoTorneioService';
import { StatusInscricao } from '../types/inscricao';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';

export interface TournamentInscriptionListProps {
  /** ID do PagamentoInscricao */
  pagamentoId: string;
}

const TournamentInscriptionList: React.FC<TournamentInscriptionListProps> = ({ pagamentoId }) => {
  const navigate = useNavigate();
  const auth = useAuth();
  const user = (auth?.user as any) || (typeof window !== 'undefined' ? JSON.parse(localStorage.getItem('user') || 'null') : null);
  const atletaId = user?.atleta?.id ?? '';

  const [isCancelling, setIsCancelling] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  const [consentDialogOpen, setConsentDialogOpen] = useState(false);
  const [consentInscricaoId, setConsentInscricaoId] = useState<string | null>(null);
  const [consentChecked, setConsentChecked] = useState(false);
  const [consentLoading, setConsentLoading] = useState(false);

  const {
    pagamento,
    inscricoes,
    ligaContaIuguAtiva,
    loading,
    error,
    refetch,
  } = usePagamentoComStatusContaIugu(pagamentoId);

  const isExpired = pagamento?.dataVencimento
    ? dayjs().isAfter(dayjs(pagamento.dataVencimento).endOf('day'))
    : false;
  const isExpiringSoon = pagamento?.dataVencimento && !isExpired
    ? dayjs(pagamento.dataVencimento).diff(dayjs(), 'hour') < 6 && dayjs(pagamento.dataVencimento).isAfter(dayjs())
    : false;

  const hasUnconsentedInscricao = inscricoes.some(
    (ins) => !ins.compartilharInformacoes && ins.status !== StatusInscricao.CANCELADA
  );

  const handlePay = () => {
    navigate('/athlete/newinscriptionpayment', { state: { pagamentoId } });
  };

  const handleCancel = async () => {
    if (!pagamentoId) return;
    const confirmar = window.confirm(
      'Tem certeza que deseja cancelar este pagamento e as inscrições ligadas a ele?'
    );
    if (!confirmar) return;

    try {
      setIsCancelling(true);
      setErrorMsg(null);
      setSuccessMsg(null);

      await cancelarPagamentoEInscricoes(pagamentoId, 'Cancelado pelo atleta');

      setSuccessMsg('Pagamento e inscrições cancelados com sucesso.');
      await refetch();
    } catch (err: any) {
      console.error(err);
      setErrorMsg(err.message || 'Erro ao cancelar pagamento.');
    } finally {
      setIsCancelling(false);
    }
  };

  const handleOpenConsent = (inscricaoId: string) => {
    setConsentInscricaoId(inscricaoId);
    setConsentChecked(false);
    setConsentDialogOpen(true);
  };

  const handleConsentir = async () => {
    if (!consentInscricaoId) return;
    setConsentLoading(true);
    try {
      await consentirCompartilharInformacoes(consentInscricaoId);
      setConsentDialogOpen(false);
      setConsentInscricaoId(null);
      setConsentChecked(false);
      setSuccessMsg('Compartilhamento de informações autorizado com sucesso.');
      await refetch();
    } catch (err: any) {
      console.error(err);
      setErrorMsg(err.message || 'Erro ao autorizar compartilhamento.');
    } finally {
      setConsentLoading(false);
    }
  };

  if (loading) return <Load />;
  if (error) return <div className="alert alert-danger">Erro ao carregar inscrições: {error.message}</div>;

  if (inscricoes.length === 0) {
    return <p className="mt-3">Nenhuma inscrição encontrada.</p>;
  }

  return (
    <div className="row">
      {successMsg && (
        <div className="alert alert-success text-center">{successMsg}</div>
      )}
      {errorMsg && (
        <div className="alert alert-danger text-center">{errorMsg}</div>
      )}
      {inscricoes.map((inscricao) => {
        const isCurrentUserInTeam = inscricao.equipe?.atletas?.some(atl => atl.id === atletaId) ?? false;
        const needsConsent = !inscricao.compartilharInformacoes && inscricao.status !== StatusInscricao.CANCELADA;

        return (
          <div key={inscricao.id} className="col-12 col-md-6 col-lg-4 mb-3">
            <div className="card w-100 shadow-xss rounded-xxl border-0 p-3" style={{ minHeight: 200 }}>
              <div className="mb-2">
                <h6 className="fw-700 text-grey-900 mb-0">{inscricao.categoriaTorneio?.nome}</h6>
                <div className="font-xssss fw-600 mt-1">
                  Valor: R$ {(inscricao.categoriaTorneio?.precoInscricao ?? 0).toFixed(2)}
                </div>
              </div>

              <div className="d-flex gap-2 mb-2">
                {inscricao.equipe?.atletas?.map((atl) => (
                  <div
                    key={atl.id}
                    className="d-flex flex-column align-items-center text-center"
                    style={{ flex: '1 1 0', minWidth: 0 }}
                  >
                    <img
                      src={atl.usuario?.imagemUrl || '/assets/images/user.png'}
                      alt={atl.usuario?.nome ?? 'Atleta'}
                      className="rounded-circle mb-1"
                      style={{ width: 40, height: 40, objectFit: 'cover' }}
                    />
                    <span
                      className="fw-600 text-grey-900 font-xssss"
                      style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '100%' }}
                    >
                      {atl.usuario?.nome ?? 'Atleta'}
                    </span>
                    {atl.usuario?.username && (
                      <span
                        className="font-xssss text-grey-500"
                        style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '100%' }}
                      >
                        @{atl.usuario.username}
                      </span>
                    )}
                  </div>
                ))}
              </div>

              {pagamento?.status === StatusInscricao.PENDENTE && isCurrentUserInTeam && inscricao.status !== StatusInscricao.FILA_ESPERA && (
                <div className="mt-3 mt-auto">
                  {!ligaContaIuguAtiva && (
                    <div className="alert alert-info mb-2 py-2" role="alert">
                      Esta Liga não está com uma conta de pagamento ativa.
                    </div>
                  )}
                  {needsConsent && (
                    <div className="alert alert-warning mb-2 py-1 font-xssss" role="alert">
                      Você precisa autorizar o compartilhamento de informações antes de pagar.{' '}
                      <button
                        className="btn btn-link p-0 fw-600 font-xssss"
                        onClick={() => handleOpenConsent(inscricao.id)}
                      >
                        Autorizar agora
                      </button>
                    </div>
                  )}
                  {pagamento?.dataVencimento && !isExpired && (
                    <div className="font-xssss text-grey-500 mb-2">
                      <i className="bi bi-clock me-1"></i>
                      Vencimento: {dayjs(pagamento.dataVencimento).format('DD/MM/YYYY HH:mm')}
                    </div>
                  )}
                  {isExpiringSoon && (
                    <div className="alert alert-warning mb-2 py-1 font-xssss" role="alert">
                      <i className="bi bi-exclamation-triangle me-1"></i>
                      Prazo de pagamento expira em breve!
                    </div>
                  )}
                  {isExpired && (
                    <div className="alert alert-danger mb-2 py-1 font-xssss" role="alert">
                      Prazo de pagamento expirado.
                    </div>
                  )}
                  <div className="d-flex gap-2">
                    <button
                      id={`pay-${inscricao.id}`}
                      className="btn btn-primary w-100"
                      onClick={handlePay}
                      disabled={isCancelling || !ligaContaIuguAtiva || isExpired || hasUnconsentedInscricao}
                    >
                      PAGAR INSCRIÇÃO
                    </button>
                    <button
                      className="btn btn-outline-secondary w-100"
                      onClick={handleCancel}
                      disabled={isCancelling}
                    >
                      {isCancelling ? 'CANCELANDO...' : 'CANCELAR'}
                    </button>
                  </div>
                </div>
              )}
              {inscricao.status === StatusInscricao.FILA_ESPERA && isCurrentUserInTeam && (
                <div className="mt-3 mt-auto">
                  <div className="text-center mb-2">
                    <span className="badge" style={{ backgroundColor: 'rgba(23,162,184,0.2)', color: '#17a2b8' }}>
                      Fila de Espera
                    </span>
                  </div>
                  <button
                    className="btn btn-outline-secondary w-100"
                    onClick={handleCancel}
                    disabled={isCancelling}
                  >
                    {isCancelling ? 'CANCELANDO...' : 'CANCELAR'}
                  </button>
                </div>
              )}
              {inscricao.status === StatusInscricao.CONFIRMADA && (
                <div className="text-center mt-3">
                  <span className="badge bg-success">Inscrição Confirmada</span>
                </div>
              )}
              {inscricao.status === StatusInscricao.CANCELADA && (
                <div className="text-center mt-3">
                  <span className="badge bg-warning">Inscrição Cancelada</span>
                </div>
              )}
              {isCurrentUserInTeam && (
                <div className="mt-2 border-top pt-2">
                  <button
                    className="btn btn-link btn-sm text-primary p-0 d-block font-xssss"
                    onClick={() => navigate(`/athlete/inscricao/${inscricao.id}`)}
                  >
                    Ver detalhes
                  </button>
                </div>
              )}
            </div>
          </div>
        );
      })}

      <Dialog
        open={consentDialogOpen}
        onClose={() => setConsentDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Compartilhamento de informações</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Para que a organização do torneio possa entrar em contato com você,
            é necessário autorizar o compartilhamento das suas informações de
            <strong> e-mail</strong> e <strong>telefone</strong>.
          </Typography>
          <FormControlLabel
            control={
              <Checkbox
                checked={consentChecked}
                onChange={(e) => setConsentChecked(e.target.checked)}
                disabled={consentLoading}
              />
            }
            label="Autorizo o compartilhamento das minhas informações de e-mail e telefone com a organização do torneio para fins de comunicação e gestão do evento."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConsentDialogOpen(false)} disabled={consentLoading}>
            Cancelar
          </Button>
          <Button
            onClick={handleConsentir}
            variant="contained"
            disabled={!consentChecked || consentLoading}
          >
            {consentLoading ? 'Autorizando...' : 'Autorizar'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default TournamentInscriptionList;
